<template>
  <v-card flat class="modern-card overflow-hidden">
    <!-- Premium Header -->
    <v-sheet color="grey lighten-4" class="px-8 py-5 border-bottom d-flex align-center">
      <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
        <v-icon color="white" small>fas fa-check-double</v-icon>
      </v-avatar>
      <div>
        <div class="text-h5 font-weight-black primary--text lh-1">
          Aprobar Cotización #{{ salesNote.id }}
        </div>
        <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
          Confirmación de negocio y generación de OT
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon color="grey darken-1" class="grey lighten-3" @click="$emit('close')">
        <v-icon small>fas fa-times</v-icon>
      </v-btn>
    </v-sheet>

    <v-card-text class="pa-0">
      <!-- Sales Detail Component (Read Only Reference) -->
      <div class="px-8 pt-8">
        <SalesDetail :salesNote="salesNote" :approbing="true" @close="$emit('close')" />
      </div>

      <v-divider class="mx-8 my-8"></v-divider>

      <!-- Work Order Configuration Form -->
      <div class="px-8 pb-10">
        <!-- <div class="d-flex align-center mb-6">
          <v-icon small color="primary" class="mr-2">fas fa-hard-hat</v-icon>
          <span class="text-subtitle-1 font-weight-black primary--text text-uppercase tracking-tight">Detalles de la Orden de Trabajo</span>
          <v-spacer></v-spacer>
          <delivery-estimation :products="salesNote.details.products" />
        </div> -->

        <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
          <v-alert v-if="!salesNote.contact" type="error" dense outlined class="mb-6">
            <div class="d-flex align-center">
              <v-icon color="error" class="mr-3">fas fa-exclamation-circle</v-icon>
              <div>
                <strong>No se puede confirmar el negocio:</strong> Esta cotización no tiene un contacto asignado.
                <div class="caption">Debe modificar la cotización y asignarle un contacto antes de generar la orden de trabajo.</div>
              </div>
            </div>
          </v-alert>

          <v-form @submit.prevent="handleSubmit(save)">
            <v-row dense>
              <v-col cols="12" md="4">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Orden de Compra</div>
                <v-text-field
                  v-model="purchase_order"
                  placeholder="N° OC del Cliente"
                  outlined dense
                  class="modern-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha de Entrega</div>
                <ValidationProvider name="Fecha de Entrega" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="delivery_date"
                    type="datetime-local"
                    outlined dense
                    class="modern-input"
                    :error-messages="errors"
                    hide-details="auto"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="4">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo de Despacho</div>
                <v-select
                  v-model="delivery_type"
                  :items="deliveryTypeList"
                  item-text="name"
                  item-value="value"
                  outlined dense
                  class="modern-select"
                  hide-details="auto"
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-4">
              <v-col cols="12" md="6">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Instalación Programada</div>
                <v-text-field
                  v-model="install_date"
                  type="datetime-local"
                  outlined dense
                  class="modern-input"
                  hide-details="auto"
                  placeholder="Opcional"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desinstalación Programada</div>
                <v-text-field
                  v-model="uninstall_date"
                  type="datetime-local"
                  outlined dense
                  class="modern-input"
                  hide-details="auto"
                  placeholder="Opcional"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row dense class="mt-6 align-center">
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 bg-lightgrey border-dashed">
                  <div class="d-flex align-center">
                    <v-checkbox v-model="customer_approval" hide-details class="mt-0 mr-4" />
                    <div class="flex-grow-1">
                      <div class="caption font-weight-black primary--text text-uppercase mb-1">Visto Bueno del Cliente</div>
                      <v-text-field
                        v-model="approval_notes"
                        :disabled="!customer_approval"
                        placeholder="Nombre de quién autoriza..."
                        dense outlined hide-details
                        class="modern-input-small"
                      ></v-text-field>
                    </div>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6" v-if="preproduction_active">
                <v-card outlined class="pa-4 bg-lightgrey border-dashed h-100 d-flex align-center">
                  <v-checkbox v-model="in_prestage" color="primary" hide-details class="mt-0 mr-2" />
                  <div>
                    <div class="body-2 font-weight-bold primary--text">Saltar Pre-producción</div>
                    <div class="caption grey--text">Iniciar directamente en etapa productiva</div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-row dense class="mt-6">
              <v-col cols="12">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Observaciones para Producción</div>
                <v-textarea
                  v-model="notes"
                  outlined dense
                  rows="3"
                  class="modern-input"
                  placeholder="Detalles importantes para el equipo de producción..."
                  hide-details="auto"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Final Form Actions -->
            <div class="d-flex align-center justify-end mt-10 gap-3">
              <v-btn
                text
                large
                @click="$emit('close')"
                class="font-weight-black text-uppercase tracking-widest grey--text px-6"
              >
                Cancelar
              </v-btn>
              <v-btn
                type="submit"
                color="success"
                large
                :loading="loading"
                :disabled="!salesNote.contact"
                class="font-weight-black text-uppercase tracking-widest px-10 shadow-sm rounded-lg"
              >
                <v-icon left small>fas fa-thumbs-up</v-icon>
                Confirmar Negocio
              </v-btn>
            </div>
          </v-form>
        </ValidationObserver>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import notifications from '@/utils/notifications'
import SalesDetail from '@/components/sales/SalesDetail'
import { createWorkOrder, getWorkOrderEstimate } from '@/api/production'
import DeliveryEstimation from '@/components/sales/DeliveryEstimation.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { canConfirmSalesNote } from '@/utils/quotationApproval'

export default {
  name: 'ApproveSalesNote',
  components: { SalesDetail, DeliveryEstimation, ValidationObserver, ValidationProvider },
  props: ['salesNote'],
  data() {
    return {
      purchase_order: '',
      delivery_date: '',
      deliveryTypeList: [
        { name: 'Despacho', value: 0 },
        { name: 'Retira el Cliente', value: 1 },
        { name: 'Retira Instalador', value: 2 },
      ],
      delivery_type: 0,
      install_date: '',
      uninstall_date: '',
      notes: '',
      customer_approval: false,
      approval_notes: '',
      in_prestage: false,
      loading: false,
      preproduction_active: this.$store.getters.getCompany.preproduction_active,
    }
  },
  methods: {
    async save() {
      if (!this.salesNote.contact) {
        notifications.showError('No se puede confirmar el negocio: la cotización no tiene un contacto asignado')
        return
      }
      if (!canConfirmSalesNote(this.$store, this.salesNote)) {
        notifications.showError('La cotización debe estar aprobada internamente antes de confirmar el negocio')
        return
      }
      this.loading = true
      const salesNoteData = {
        sales_note: this.salesNote.id,
        delivery_date: new Date(this.delivery_date).toISOString(),
        purchase_order: this.purchase_order,
        customer_approval: this.customer_approval,
        approval_notes: this.approval_notes,
        notes: this.notes,
        delivery_type: this.delivery_type,
        in_prestage: this.preproduction_active ? !this.in_prestage : false
      }
      if (this.install_date) salesNoteData.install_date = new Date(this.install_date).toISOString()
      if (this.uninstall_date) salesNoteData.uninstall_date = new Date(this.uninstall_date).toISOString()

      try {
        const data = await createWorkOrder(salesNoteData)
        this.$emit('close', data)
        if (data.status) {
          notifications.showSuccess(`Orden de trabajo ${data.work_order} creada con éxito`)
        } else {
          notifications.showWarn(`Orden de trabajo ${data.work_order} creada con problemas de stock`)
          if (data.notified) {
            notifications.showInfo('Notificación enviada por correo electrónico')
          } else {
            notifications.showError('No se pudo enviar notificación por correo electrónico')
          }
        }
      } catch (e) {
        notifications.showError('Error creando Orden de Trabajo ' + e)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    // Set a default delivery date if available from estimate or current time + 7 days
    if (!this.delivery_date) {
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 7)
      this.delivery_date = defaultDate.toISOString().slice(0, 16)
    }
  }
}
</script>

<style lang="scss" scoped>
.modern-card { border-radius: 12px !important; background: white; font-family: 'Outfit', sans-serif !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.border-white-sm { border: 2px solid white !important; }
.border-dashed { border: 1px dashed #E2E8F0 !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.gap-3 { gap: 12px; }
.tracking-tight { letter-spacing: -0.5px; }
.tracking-widest { letter-spacing: 1px !important; }

/* Unified Input Styles */
.modern-input, .modern-select {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 8px !important;
      background-color: #F8FAFC !important;
      border: 1px solid #E2E8F0 !important;
      box-shadow: none !important;
      fieldset { border: none; }
    }
  }
}

.modern-input-small {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 6px !important;
      background-color: white !important;
      border: 1px solid #E2E8F0 !important;
      fieldset { border: none; }
      min-height: 32px !important;
      padding: 0 8px !important;
    }
    input { font-size: 0.8rem !important; }
  }
}
</style>